'use client'

import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useSelectedTickerInfo } from '@/src/hooks/token-info/use-selected-ticker'
import {
  invalidWalletKeyringAtom,
  isActiveAtom,
  isCompleteAtom,
} from '@/src/stores/route-machine/atoms'
import { useNetworkSelection } from '@/src/stores/token-selection/actions'
import { cn } from '@/src/utils/styles'
import { ChainImg } from '../ui/chain-img'

const NETWORK_THRESHOLD_BEFORE_MARQUEE = 4

function NetworkAvailabilitySectionSkeleton() {
  return (
    <div className='flex gap-3 border border-border1 rounded-3xl w-full h-[72px] items-center px-5 justify-between'>
      <span className='text-sm text-secondary'>Loading networks</span>
      <div className='flex items-center gap-3 py-1'>
        {Array(3)
          .fill(null)
          .map((_, i) => (
            <div
              key={`ns-loader-${i}`}
              className='flex items-center justify-center size-8 bg-surface2 rounded-[8px]'
            >
              <div
                className='rounded-full size-5 bg-tertiary animate-pulse duration-1000'
                style={{ animationDelay: `${i * 100}ms` }}
              />
            </div>
          ))}
      </div>
    </div>
  )
}

export default function NetworkAvailabilitySection() {
  const { isLoading, chains, activeToken, primaryToken } =
    useSelectedTickerInfo()

  const shouldMarquee = useMemo(
    () => chains.length >= NETWORK_THRESHOLD_BEFORE_MARQUEE,
    [chains.length],
  )

  const networks = useMemo(() => {
    if (!chains) return

    if (chains.length < 8 && shouldMarquee) {
      const data = Array(16)
        .fill(null)
        .map((_, i) => chains[i % chains.length])
      return {
        length: chains.length,
        data,
      }
    }

    if (!shouldMarquee) {
      return {
        length: chains.length,
        data: chains,
      }
    }

    return {
      length: chains.length,
      data: chains.concat(chains),
    }
  }, [chains, shouldMarquee])

  const { open } = useNetworkSelection()

  const isTradeActive = useAtomValue(isActiveAtom)
  const isComplete = useAtomValue(isCompleteAtom)
  const invalidWalletKeyring = useAtomValue(invalidWalletKeyringAtom)

  const isDisplayingTradeOverview =
    isTradeActive || isComplete || !!invalidWalletKeyring

  if (isLoading) return <NetworkAvailabilitySectionSkeleton />

  if (!networks) {
    return (
      <div className='flex gap-3 border border-border1 rounded-3xl w-full h-28 items-center px-5 justify-between'>
        <span className='text-sm text-secondary'>
          Error while fetching networks
        </span>
      </div>
    )
  }

  if (networks.length === 1) {
    const element = networks.data[0]

    if (!element?.chain_id) return

    return (
      <div className='flex gap-3 px-5 border border-border1 rounded-3xl h-[72px] w-full items-center justify-between'>
        <span className='text-sm text-secondary'>
          Only available on {element?.chain_name}
        </span>
        <ChainImg
          chainImg={element?.chain_img}
          chainId={element.chain_id}
          className='size-5'
          containerClassName='size-8'
          unoptimized
        />
      </div>
    )
  }

  return (
    <button
      type='button'
      onClick={() => {
        if (isDisplayingTradeOverview) return

        open({
          direction: activeToken,
          chainId: primaryToken?.chain_id,
          denom: primaryToken?.denom,
          displayName: primaryToken?.display_name,
          ticker: primaryToken?.ticker,
          tokenImg: primaryToken?.logo_img_url,
        })
      }}
      disabled={isDisplayingTradeOverview}
      className='group flex gap-3 cursor-pointer border border-border1 rounded-3xl h-[72px] w-full items-center justify-between hover:border-transparent hover:bg-surface1-100 disabled:pointer-events-none transition-colors'
    >
      <span className='text-secondary group-hover:text-primary transition-colors text-sm pl-5'>
        Available on {networks?.length} networks
      </span>
      <div
        className={cn('flex pb-5 w-44 relative overflow-hidden h-8', {
          'pr-5 justify-end': !shouldMarquee,
          'after:absolute after:-left-0.5 after:w-9 after:h-8 after:bg-gradient-to-r after:group-hover:from-surface1-100 after:from-background after:to-transparent after:z-10 after:transition-colors':
            shouldMarquee,
        })}
      >
        <div
          className={cn('flex items-center gap-3 absolute hover:paused', {
            'animate-marquee': shouldMarquee,
          })}
          style={{
            // @ts-expect-error
            '--marquee-elements': networks.length,
            '--marquee-elements-m1': networks.length - 1,
            '--marquee-elements-gap': '12px',
            '--marquee-element-size': '32px',
          }}
        >
          {networks.data.map((chain, i) => {
            if (!chain) return

            return (
              <ChainImg
                key={`ns-element-${i}`}
                chainImg={chain.chain_img}
                chainId={chain.chain_id}
                className='size-5'
                containerClassName='size-8'
                unoptimized
              />
            )
          })}
        </div>
      </div>
    </button>
  )
}
